import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Contacts as ContactsIcon,
  Business as TenantsIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const AccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccount();
  }, [id]);

  const fetchAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/accounts/${id}`);
      setAccount(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load account');
      toast.error('Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!account) return;
    if (!window.confirm(`Delete account "${account.account_name}"? This cannot be undone.`)) return;
    api
      .delete(`/accounts/${account.id}`)
      .then(() => {
        toast.success('Account deleted');
        navigate('/accounts');
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Failed to delete account'));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/accounts')} sx={{ mb: 2 }}>
          Back to Accounts
        </Button>
        <Alert severity="error">{error || 'Account not found'}</Alert>
      </Box>
    );
  }

  const contacts = Array.isArray(account.contacts) ? account.contacts : [];
  const tenants = Array.isArray(account.tenants) ? account.tenants : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/accounts')} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Account Details
        </Typography>
        <Box sx={{ flex: 1 }} />
        {can('accounts', 'edit') && (
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate('/accounts', { state: { editAccountId: account.id } })}
        >
          Edit
        </Button>
        )}
        {can('accounts', 'delete') && (
        <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
          Delete
        </Button>
        )}
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Basic Info
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Account ID</Typography>
            <Typography variant="body1" fontWeight={600}>{account.account_code || account.id}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Account Name</Typography>
            <Typography variant="body1" fontWeight={600}>{account.account_name}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Company Name</Typography>
            <Typography variant="body1">{account.company_name || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Website</Typography>
            <Typography variant="body1">{account.website ? (
              <a href={account.website.startsWith('http') ? account.website : `https://${account.website}`} target="_blank" rel="noopener noreferrer">{account.website}</a>
            ) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Office Direct Number</Typography>
            <Typography variant="body1">{account.office_direct_number || '—'}</Typography>
          </Box>
        </Box>
        {account.company_address && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Company Address</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{account.company_address}</Typography>
          </Box>
        )}
        {account.notes && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{account.notes}</Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Contacts ({contacts.length})
          </Typography>
          <Button
            size="small"
            startIcon={<ContactsIcon />}
            onClick={() => navigate(`/accounts/${account.id}/contacts`)}
          >
            Manage Contacts
          </Button>
        </Box>
        {contacts.length === 0 ? (
          <Typography color="text.secondary">No contacts. Add contacts from Manage Contacts.</Typography>
        ) : (
          <List dense>
            {contacts.slice(0, 10).map((c) => (
              <ListItem key={c.id}>
                <ListItemText
                  primary={`${c.first_name || ''} ${c.last_name || ''}`}
                  secondary={c.email}
                />
              </ListItem>
            ))}
            {contacts.length > 10 && (
              <ListItem>
                <Button size="small" onClick={() => navigate(`/accounts/${account.id}/contacts`)}>
                  View all {contacts.length} contacts
                </Button>
              </ListItem>
            )}
          </List>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Tenants ({tenants.length})
          </Typography>
          <Button
            size="small"
            startIcon={<TenantsIcon />}
            onClick={() => navigate('/tenants')}
          >
            View Tenants
          </Button>
        </Box>
        {tenants.length === 0 ? (
          <Typography color="text.secondary">No tenants linked to this account.</Typography>
        ) : (
          <List dense>
            {tenants.slice(0, 10).map((t) => (
              <ListItem
                key={t.id}
                button
                onClick={() => navigate(`/tenants/${t.id}`)}
              >
                <ListItemText
                  primary={t.tenant_name || t.tenant_id}
                  secondary={t.status ? <Chip label={t.status} size="small" /> : null}
                />
              </ListItem>
            ))}
            {tenants.length > 10 && (
              <ListItem>
                <Button size="small" onClick={() => navigate('/tenants')}>
                  View all tenants
                </Button>
              </ListItem>
            )}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default AccountDetail;
